import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { resumeService } from '../services/resume.service';
import { applyService } from '../services/apply.service';
import { Resume, ResumeData } from '../types';
import { ArrowLeft, Download, Share2 } from 'lucide-react';
import { ResumeForm } from '../components/ResumeForm';
import { ResumePreview } from '../components/ResumePreview';

type OptimizeTarget =
    | { type: 'summary' }
    | { type: 'bullet'; jobIndex: number; bulletIndex: number };

export function EditorPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [resume, setResume] = useState<Resume | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [error, setError] = useState('');
    const [saveTimeout, setSaveTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [mobilePanel, setMobilePanel] = useState<'form' | 'preview'>('form');
    const [isSharing, setIsSharing] = useState(false);
    const [copied, setCopied] = useState(false);

    const [optimizeTarget, setOptimizeTarget] = useState<OptimizeTarget | null>(null);
    const [optimizeText, setOptimizeText] = useState('');
    const [optimizeInstruction, setOptimizeInstruction] = useState('');
    const [isOptimizing, setIsOptimizing] = useState(false);
    const [optimizingSummary, setOptimizingSummary] = useState(false);
    const [optimizingBulletKey, setOptimizingBulletKey] = useState<string | null>(null);

    useEffect(() => {
        if (id) {
            loadResume();
        }
    }, [id]);

    const loadResume = async () => {
        try {
            const data = await resumeService.getResumeById(id!);
            setResume(data);
        } catch (err: any) {
            setError('Failed to load resume');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDataChange = (newData: Partial<ResumeData>) => {
        if (!resume) return;

        const updatedResume = {
            ...resume,
            data: {
                ...resume.data,
                ...newData,
            },
        };
        setResume(updatedResume);
        setHasUnsavedChanges(true);

        if (saveTimeout) {
            clearTimeout(saveTimeout);
        }

        const timeout = setTimeout(() => {
            saveResume(updatedResume);
        }, 1000);

        setSaveTimeout(timeout);
    };

    const handleTitleChange = (title: string) => {
        if (!resume) return;
        const updatedResume = { ...resume, title };
        setResume(updatedResume);
        setHasUnsavedChanges(true);

        if (saveTimeout) {
            clearTimeout(saveTimeout);
        }

        const timeout = setTimeout(() => {
            saveResume(updatedResume);
        }, 1000);

        setSaveTimeout(timeout);
    };

    const handleTemplateChange = (template: 'ats' | 'modern') => {
        if (!resume) return;
        const updatedResume = { ...resume, template };
        setResume(updatedResume);
        saveTemplate(template);
    };

    const saveTemplate = async (template: 'ats' | 'modern') => {
        if (!resume) return;
        setIsSaving(true);
        try {
            await resumeService.updateResume(resume.id, { template });
        } catch (err: any) {
            console.error('Failed to save template:', err);
        } finally {
            setIsSaving(false);
        }
    };

    const saveResume = async (resumeToSave: Resume) => {
        setIsSaving(true);
        try {
            await resumeService.updateResume(resumeToSave.id, {
                title: resumeToSave.title,
                template: resumeToSave.template,
                data: resumeToSave.data,
            });
            setHasUnsavedChanges(false);
        } catch (err: any) {
            console.error('Failed to save:', err);
        } finally {
            setIsSaving(false);
        }
    };

    const openOptimizeSummary = (summary: string) => {
        setOptimizeTarget({ type: 'summary' });
        setOptimizeText(summary || '');
        setOptimizeInstruction('');
    };

    const openOptimizeBullet = (jobIndex: number, bulletIndex: number, bulletText: string) => {
        setOptimizeTarget({ type: 'bullet', jobIndex, bulletIndex });
        setOptimizeText(bulletText || '');
        setOptimizeInstruction('');
    };

    const closeOptimizeModal = () => {
        if (isOptimizing) return;
        setOptimizeTarget(null);
        setOptimizeText('');
        setOptimizeInstruction('');
    };

    const applyOptimizedText = (optimized: string) => {
        if (!resume || !optimizeTarget) return;

        if (optimizeTarget.type === 'summary') {
            handleDataChange({ summary: optimized });
            return;
        }

        const updatedExperience = [...resume.data.experience];
        const targetJob = updatedExperience[optimizeTarget.jobIndex];
        if (!targetJob) return;

        const updatedBullets = [...targetJob.bullets];
        updatedBullets[optimizeTarget.bulletIndex] = optimized;
        updatedExperience[optimizeTarget.jobIndex] = {
            ...targetJob,
            bullets: updatedBullets,
        };
        handleDataChange({ experience: updatedExperience });
    };

    const handleOptimizeSubmit = async () => {
        if (!optimizeTarget) return;
        const inputText = optimizeText.trim();
        if (!inputText) return;

        setIsOptimizing(true);
        if (optimizeTarget.type === 'summary') {
            setOptimizingSummary(true);
        } else {
            setOptimizingBulletKey(`${optimizeTarget.jobIndex}:${optimizeTarget.bulletIndex}`);
        }

        try {
            const result = await applyService.optimizeText({
                text: inputText,
                fieldType: optimizeTarget.type,
                instruction: optimizeInstruction.trim() || undefined,
            });
            applyOptimizedText(result.optimizedText);
            closeOptimizeModal();
        } catch (err: any) {
            console.error('Failed to optimize text:', err);
            alert(err?.response?.data?.message || 'Failed to optimize text.');
        } finally {
            setIsOptimizing(false);
            setOptimizingSummary(false);
            setOptimizingBulletKey(null);
        }
    };

    const handleExport = async (format: 'pdf' | 'docx') => {
        if (!resume) return;

        try {
            const blob = format === 'pdf' ? await resumeService.exportPdf(resume.id) : await resumeService.exportDocx(resume.id);

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${resume.title}.${format}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err: any) {
            alert(`Failed to export ${format.toUpperCase()}`);
            console.error(err);
        }
    };

    const publicUrl = resume?.public_id ? `${window.location.origin}/r/${resume.public_id}` : '';

    const handleShare = async () => {
        if (!resume) return;
        setIsSharing(true);
        try {
            const updated = await resumeService.shareResume(resume.id);
            setResume(updated);
            if (updated.public_id) {
                const url = `${window.location.origin}/r/${updated.public_id}`;
                await navigator.clipboard.writeText(url);
                alert('Public link copied!');
            } else {
                alert('Public link created. Use the share button again to copy.');
            }
        } catch (err: any) {
            console.error('Failed to share resume:', err);
            alert('Failed to create share link.');
        } finally {
            setIsSharing(false);
        }
    };

    const handleUnshare = async () => {
        if (!resume) return;
        setIsSharing(true);
        try {
            const updated = await resumeService.unshareResume(resume.id);
            setResume(updated);
        } catch (err: any) {
            console.error('Failed to disable share:', err);
            alert('Failed to disable sharing.');
        } finally {
            setIsSharing(false);
        }
    };

    const missingSections = ((location.state as { missingSections?: string[] })?.missingSections || []);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    if (error || !resume) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-600 mb-4">{error || 'Resume not found'}</p>
                    <button onClick={() => navigate('/dashboard')} className="btn btn-primary">
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
            <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
                <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate('/dashboard')} className="text-gray-600 hover:text-gray-900">
                                <ArrowLeft size={24} />
                            </button>
                            <input
                                type="text"
                                value={resume.title}
                                onChange={(e) => handleTitleChange(e.target.value)}
                                className="text-xl font-semibold border-none bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 rounded px-2"
                            />
                            {isSaving && <span className="text-sm text-gray-500">Saving...</span>}
                            {!isSaving && hasUnsavedChanges && <span className="text-sm text-amber-600">Unsaved changes</span>}
                            {!isSaving && !hasUnsavedChanges && <span className="text-sm text-emerald-600">All changes saved</span>}
                        </div>

                        <div className="flex items-center gap-2">
                            <button onClick={() => resume && saveResume(resume)} className="btn btn-primary" disabled={isSaving || !hasUnsavedChanges}>
                                Save
                            </button>
                            <select
                                value={resume.template}
                                onChange={(e) => handleTemplateChange(e.target.value as 'ats' | 'modern')}
                                className="input py-2"
                            >
                                <option value="ats">ATS-Friendly</option>
                                <option value="modern">Modern</option>
                            </select>

                            {resume.is_public ? (
                                <button onClick={handleUnshare} className="btn btn-secondary flex items-center gap-2" disabled={isSharing}>
                                    <Share2 size={18} />
                                    {isSharing ? 'Updating...' : 'Disable link'}
                                </button>
                            ) : (
                                <button onClick={handleShare} className="btn btn-secondary flex items-center gap-2" disabled={isSharing}>
                                    <Share2 size={18} />
                                    {isSharing ? 'Sharing...' : 'Share'}
                                </button>
                            )}

                            <button onClick={() => handleExport('pdf')} className="btn btn-secondary flex items-center gap-2">
                                <Download size={18} />
                                PDF
                            </button>
                            <button onClick={() => handleExport('docx')} className="btn btn-secondary flex items-center gap-2">
                                <Download size={18} />
                                DOCX
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {missingSections.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 px-6 py-3">
                    We auto-imported what was available, but we still need you to fill: {missingSections.join(', ')}.
                </div>
            )}

            {resume.is_public && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-3 text-sm flex flex-wrap items-center gap-3">
                    <span>Public link active.</span>
                    {publicUrl ? (
                        <button
                            onClick={async () => {
                                await navigator.clipboard.writeText(publicUrl);
                                setCopied(true);
                                setTimeout(() => setCopied(false), 1500);
                            }}
                            className="btn btn-secondary py-1 px-3"
                        >
                            {copied ? 'Copied' : 'Copy link'}
                        </button>
                    ) : (
                        <button onClick={handleShare} className="btn btn-secondary py-1 px-3" disabled={isSharing}>
                            Copy link
                        </button>
                    )}
                </div>
            )}

            <div className="flex-1 min-h-0 flex flex-col">
                <div className="md:hidden sticky top-0 z-10 flex items-center gap-2 px-4 py-3 bg-white border-b border-gray-200">
                    <button type="button" onClick={() => setMobilePanel('form')} className={`btn ${mobilePanel === 'form' ? 'btn-primary' : 'btn-secondary'}`}>
                        Edit
                    </button>
                    <button type="button" onClick={() => setMobilePanel('preview')} className={`btn ${mobilePanel === 'preview' ? 'btn-primary' : 'btn-secondary'}`}>
                        Preview
                    </button>
                </div>

                <div className="flex flex-1 min-h-0">
                    <div
                        className={`w-full md:w-1/2 min-h-0 overflow-y-auto p-6 border-r border-gray-200 bg-white ${
                            mobilePanel === 'form' ? 'block' : 'hidden md:block'
                        }`}
                    >
                        <ResumeForm
                            data={resume.data}
                            onChange={handleDataChange}
                            onOptimizeSummary={openOptimizeSummary}
                            onOptimizeBullet={openOptimizeBullet}
                            optimizingSummary={optimizingSummary}
                            optimizingBulletKey={optimizingBulletKey}
                        />
                    </div>

                    <div
                        className={`w-full md:w-1/2 min-h-0 overflow-y-auto p-6 bg-gray-100 ${
                            mobilePanel === 'preview' ? 'block' : 'hidden md:block'
                        }`}
                    >
                        <div className="max-w-[8.5in] mx-auto">
                            <ResumePreview data={resume.data} template={resume.template} />
                        </div>
                    </div>
                </div>
            </div>

            {optimizeTarget && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg w-full max-w-2xl p-5 space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900">Optimize with AI</h3>
                        <div>
                            <label className="label">Text to optimize</label>
                            <textarea
                                value={optimizeText}
                                onChange={(e) => setOptimizeText(e.target.value)}
                                className="input min-h-[140px]"
                                placeholder="Enter text to optimize"
                            />
                        </div>
                        <div>
                            <label className="label">Custom instruction (optional)</label>
                            <textarea
                                value={optimizeInstruction}
                                onChange={(e) => setOptimizeInstruction(e.target.value)}
                                className="input min-h-[90px]"
                                placeholder="Example: Make it more concise and quantifiable"
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button type="button" className="btn btn-secondary" onClick={closeOptimizeModal} disabled={isOptimizing}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleOptimizeSubmit}
                                disabled={isOptimizing || !optimizeText.trim()}
                            >
                                {isOptimizing ? 'Optimizing...' : 'Optimize'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
